<template>
    <div class="code-textarea-container">
        <DFormTextarea
            :id="id"
            :model-value="localText"
            :rows="rows"
            :placeholder="placeholder"
            :is-valid="effectiveIsValid"
            :invalid-feedback="effectiveInvalidFeedback"
            @update:modelValue="handleInput"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import DFormTextarea from '~@/Components/Common/Forms/DFormTextarea.vue'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    id: {
        type: String,
        default: null
    },
    modelValue: {
        type: String,
        default: null
    },
    rows: {
        type: [String, Number],
        default: 10
    },
    placeholder: {
        type: String,
        default: null
    },
    isValid: {
        type: Boolean,
        default: null
    },
    invalidFeedback: {
        type: String,
        default: null
    }
})

const emit = defineEmits(['update:modelValue'])

const prettify = (value) => {
    if (value === null || value === undefined || value === '') {
        return ''
    }
    try {
        return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
        return value
    }
}

const minify = (value) => {
    try {
        return JSON.stringify(JSON.parse(value))
    } catch {
        return null
    }
}

const localText = ref(prettify(props.modelValue))
const lastEmitted = ref(props.modelValue)
const isJsonInvalid = ref(false)

const effectiveIsValid = computed(() => {
    if (isJsonInvalid.value) {
        return false
    }
    return props.isValid
})

const effectiveInvalidFeedback = computed(() => {
    if (isJsonInvalid.value) {
        return __('validation.json', { attribute: 'JSON' })
    }
    return props.invalidFeedback
})

watch(() => props.modelValue, (newValue) => {
    if (newValue === lastEmitted.value) {
        return
    }
    localText.value = prettify(newValue)
    lastEmitted.value = newValue
    isJsonInvalid.value = false
})

const handleInput = (newText) => {
    localText.value = newText ?? ''

    if (newText === null || newText === '') {
        isJsonInvalid.value = false
        lastEmitted.value = null
        emit('update:modelValue', null)
        return
    }

    const minified = minify(newText)
    if (minified === null) {
        isJsonInvalid.value = true
        lastEmitted.value = newText
        emit('update:modelValue', newText)
        return
    }

    isJsonInvalid.value = false
    lastEmitted.value = minified
    emit('update:modelValue', minified)
}
</script>
